import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDownCircle, ArrowUpCircle, Scale, Plus, Check, Trash2, Loader2, FileSpreadsheet, Search, FilterX, Paperclip, Repeat, TrendingUp, Pencil } from "lucide-react";
import { formatBRL } from "../../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hoje = new Date().toISOString().slice(0, 10);

const CATS = {
  pagar: ["Fixas (água, luz, aluguel)", "Pessoal/Equipe", "Marketing e anúncios", "Impostos e taxas", "Veículo", "Outros"],
  receber: ["Venda de veículo", "Entrada/Sinal", "Multa de trânsito", "Serviços", "Outros"],
};

const inputCls =
  "w-full bg-[#0d0f17] border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors";

const FinColumn = ({ tipo, titulo, icon: Icon, color, items, form, setForm, saving, add, toggle, remove, onEdit, vehicles, vehicleName }) => {
  const active = form.tipo === tipo;
  const f = active ? form : { tipo, descricao: "", valor: "", vencimento: hoje, categoria: CATS[tipo][0], veiculo_id: "", fornecedor: "", parcelas: "", recorrente: false, arquivo: null };
  const editando = active && !!form.editId;
  return (
    <div className="glass rounded-2xl p-6" data-testid={`fin-col-${tipo}`}>
      <h2 className={`font-display font-bold flex items-center gap-2 mb-4 ${color}`}>
        <Icon className="w-5 h-5" /> {titulo}
      </h2>

      <div className="space-y-2 pb-4 mb-4 border-b border-slate-800/60">
        {editando && (
          <div className="flex items-center justify-between bg-sky-500/10 border border-sky-500/30 rounded-xl px-3 py-2" data-testid="fin-editando-aviso">
            <span className="text-[11px] font-semibold text-sky-400">Editando lançamento</span>
            <button type="button" onClick={() => setForm({ tipo, descricao: "", valor: "", vencimento: hoje, categoria: CATS[tipo][0], veiculo_id: "", fornecedor: "", parcelas: "", recorrente: false, arquivo: null })} data-testid="fin-edit-cancel" className="text-sky-400 hover:text-white text-[11px] font-semibold">Cancelar</button>
          </div>
        )}
        <input className={inputCls} placeholder="Descrição" value={f.descricao} onChange={(e) => setForm({ ...f, tipo, descricao: e.target.value })} data-testid={`fin-input-desc-${tipo}`} />
        <input
          className={inputCls}
          placeholder={tipo === "pagar" ? "Fornecedor (ex: oficina, concessionária)" : "Cliente (ex: quem vai pagar)"}
          value={f.fornecedor}
          onChange={(e) => setForm({ ...f, tipo, fornecedor: e.target.value })}
          data-testid={`fin-input-fornecedor-${tipo}`}
        />
        <div className="grid grid-cols-2 gap-2">
          <input className={inputCls} type="number" placeholder={f.parcelas > 1 ? "R$ por parcela" : "R$"} value={f.valor} onChange={(e) => setForm({ ...f, tipo, valor: e.target.value })} data-testid={`fin-input-valor-${tipo}`} />
          <input className={inputCls} type="date" value={f.vencimento} onChange={(e) => setForm({ ...f, tipo, vencimento: e.target.value })} data-testid={`fin-input-data-${tipo}`} />
        </div>
        <select
          className={inputCls} value={f.categoria}
          onChange={(e) => setForm({ ...f, tipo, categoria: e.target.value, veiculo_id: "" })}
          data-testid={`fin-input-categoria-${tipo}`}
        >
          {CATS[tipo].map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {f.categoria === "Veículo" && (
          <select
            className={inputCls} value={f.veiculo_id}
            onChange={(e) => setForm({ ...f, tipo, veiculo_id: e.target.value })}
            data-testid="fin-input-veiculo"
          >
            <option value="">Qual veículo? (centro de custo)</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.nome}</option>)}
          </select>
        )}
        {!editando && (
        <div className="grid grid-cols-2 gap-2">
          <input
            className={inputCls} type="number" min="1" max="60" placeholder="Parcelas (1 = à vista)"
            value={f.parcelas} onChange={(e) => setForm({ ...f, tipo, parcelas: e.target.value, recorrente: e.target.value > 1 ? false : f.recorrente })}
            data-testid={`fin-input-parcelas-${tipo}`}
          />
          <label className={`flex items-center gap-2 text-xs px-1 ${f.parcelas > 1 ? "text-slate-600" : "text-slate-300 cursor-pointer"}`}>
            <input
              type="checkbox" disabled={f.parcelas > 1} checked={!!f.recorrente}
              onChange={(e) => setForm({ ...f, tipo, recorrente: e.target.checked })}
              data-testid={`fin-input-recorrente-${tipo}`} className="accent-emerald-500 w-4 h-4"
            />
            Repetir todo mês
          </label>
        </div>
        )}
        <div>
          <input
            type="file" accept="image/*,application/pdf" id={`fin-file-${tipo}`} className="hidden"
            onChange={(e) => setForm({ ...f, tipo, arquivo: e.target.files?.[0] || null })}
            data-testid={`fin-input-comprovante-${tipo}`}
          />
          <label
            htmlFor={`fin-file-${tipo}`}
            className={`flex items-center justify-center gap-2 border border-dashed rounded-xl px-4 py-2.5 text-xs font-semibold cursor-pointer transition-colors ${f.arquivo ? "border-emerald-500/60 text-emerald-400" : "border-slate-700 text-slate-400 hover:border-emerald-500/50 hover:text-emerald-400"}`}
            data-testid={`fin-comprovante-label-${tipo}`}
          >
            <Paperclip className="w-3.5 h-3.5" />
            {f.arquivo ? f.arquivo.name : editando ? (f.temComprovante ? "Trocar comprovante (foto ou PDF)" : "Anexar comprovante esquecido (foto ou PDF)") : "Anexar comprovante (foto ou PDF, opcional)"}
          </label>
        </div>
        <button
          onClick={() => add(tipo)} disabled={saving} data-testid={`fin-add-${tipo}`}
          className="w-full inline-flex items-center justify-center gap-1.5 bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-400 text-sm font-semibold rounded-xl py-2.5 transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {editando ? "Salvar alterações" : f.parcelas > 1 ? `Lançar ${f.parcelas} parcelas` : "Lançar"}
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid={`fin-empty-${tipo}`}>Nada por aqui neste período.</p>
      ) : (
        <ul className="space-y-1.5" data-testid={`fin-list-${tipo}`}>
          {items.map((t) => (
            <li key={t.id} className="flex items-center gap-2.5 text-sm bg-[#0d0f17] rounded-lg px-3 py-2" data-testid={`fin-item-${t.id}`}>
              <button
                onClick={() => toggle(t)}
                data-testid={`fin-toggle-${t.id}`}
                title={t.status === "pago" ? "Marcar como pendente" : "Marcar como pago"}
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                  t.status === "pago" ? "bg-emerald-500 border-emerald-500 text-emerald-950" : "border-slate-600 text-transparent hover:border-emerald-500"
                }`}
              >
                <Check className="w-3 h-3" />
              </button>
              <div className="flex-1 min-w-0 flex items-baseline gap-2">
                <p className={`font-medium truncate ${t.status === "pago" ? "text-slate-500 line-through" : "text-white"}`}>{t.descricao}</p>
                <p className="text-[11px] text-slate-500 truncate shrink-0 hidden md:block">
                  {t.vencimento?.split("-").reverse().join("/")}
                  {t.fornecedor ? ` · ${t.fornecedor}` : ""}
                  {t.categoria ? ` · ${t.categoria}` : ""}
                  {t.veiculo_id && vehicleName(t.veiculo_id) ? ` · ${vehicleName(t.veiculo_id)}` : ""}
                </p>
              </div>
              <span className={`text-sm font-semibold whitespace-nowrap ${tipo === "receber" ? "text-emerald-400" : "text-red-400"}`}>
                {formatBRL(t.valor)}
              </span>
              {t.recorrente && (
                <span title="Conta fixa — repete todo mês" className="text-sky-400 shrink-0" data-testid={`fin-recorrente-${t.id}`}>
                  <Repeat className="w-3.5 h-3.5" />
                </span>
              )}
              {t.comprovante && (
                <a href={t.comprovante} target="_blank" rel="noopener noreferrer" title="Ver comprovante" data-testid={`fin-comprovante-${t.id}`} className="text-sky-400 hover:text-sky-300 shrink-0">
                  <Paperclip className="w-3.5 h-3.5" />
                </a>
              )}
              <button onClick={() => onEdit(t)} data-testid={`fin-edit-${t.id}`} title="Editar lançamento" className="text-slate-600 hover:text-emerald-400 transition-colors shrink-0">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => remove(t.id)} data-testid={`fin-delete-${t.id}`} className="text-slate-600 hover:text-red-400 transition-colors shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const FinanceiroTab = ({ onUnauthorized }) => {
  const [txs, setTxs] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [mes, setMes] = useState(hoje.slice(0, 7));
  const [form, setForm] = useState({ tipo: "receber", descricao: "", valor: "", vencimento: hoje, categoria: CATS.receber[0], veiculo_id: "", fornecedor: "", parcelas: "", recorrente: false, arquivo: null });
  const [saving, setSaving] = useState(false);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [fDe, setFDe] = useState("");
  const [fAte, setFAte] = useState("");
  const [fTipo, setFTipo] = useState("todos");
  const [fCat, setFCat] = useState("");
  const [fBusca, setFBusca] = useState("");
  const headers = { Authorization: `Bearer ${localStorage.getItem("seed_admin_token")}` };

  const load = useCallback(async () => {
    try {
      const [t, v] = await Promise.all([
        axios.get(`${API}/transactions`, { headers }),
        axios.get(`${API}/vehicles/admin`, { headers }),
      ]);
      setTxs(t.data);
      setVehicles(v.data);
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else { setTxs([]); toast.error("Erro ao carregar financeiro."); }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const vehicleName = (id) => vehicles.find((v) => v.id === id)?.nome;

  const periodoLivre = !!(fDe && fAte);
  const temFiltro = periodoLivre || fTipo !== "todos" || !!fCat || !!fBusca.trim();

  const noPeriodo = (t) => periodoLivre
    ? (t.vencimento || "") >= fDe && (t.vencimento || "") <= fAte
    : (t.vencimento || "").startsWith(mes);

  const buscaLower = fBusca.trim().toLowerCase();
  const filtradas = (txs || [])
    .filter(noPeriodo)
    .filter((t) => (fTipo === "todos" ? true : t.tipo === fTipo))
    .filter((t) => (fCat ? (t.categoria || "Outros") === fCat : true))
    .filter((t) => (buscaLower
      ? (t.descricao || "").toLowerCase().includes(buscaLower) || (t.fornecedor || "").toLowerCase().includes(buscaLower)
      : true));

  const entradas = filtradas.filter((t) => t.tipo === "receber" && t.status === "pago").reduce((s, t) => s + t.valor, 0);
  const saidas = filtradas.filter((t) => t.tipo === "pagar" && t.status === "pago").reduce((s, t) => s + t.valor, 0);

  const gastosPorCategoria = {};
  filtradas.filter((t) => t.tipo === "pagar" && t.status === "pago").forEach((t) => {
    const c = t.categoria || "Outros";
    gastosPorCategoria[c] = (gastosPorCategoria[c] || 0) + t.valor;
  });

  const todasCategorias = [...new Set([...CATS.pagar, ...CATS.receber, ...(txs || []).map((t) => t.categoria || "Outros")])];

  const MESES_CURTOS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  const acumulado = (() => {
    if (!txs) return [];
    const agora = new Date();
    const meses = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(agora.getFullYear(), agora.getMonth() - i, 1);
      meses.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    const pagas = txs.filter((t) => t.status === "pago");
    let acc = pagas
      .filter((t) => (t.vencimento || "") < `${meses[0]}-01`)
      .reduce((s, t) => s + (t.tipo === "receber" ? t.valor : -t.valor), 0);
    return meses.map((m) => {
      const saldo = pagas
        .filter((t) => (t.vencimento || "").startsWith(m))
        .reduce((s, t) => s + (t.tipo === "receber" ? t.valor : -t.valor), 0);
      acc += saldo;
      return { m, saldo, acc };
    });
  })();
  const maxAcc = Math.max(1, ...acumulado.map((a) => Math.abs(a.acc)));

  const limparFiltros = () => { setFDe(""); setFAte(""); setFTipo("todos"); setFCat(""); setFBusca(""); };

  const startEdit = (t) => {
    setForm({ tipo: t.tipo, editId: t.id, descricao: t.descricao, valor: t.valor, vencimento: t.vencimento, categoria: t.categoria || CATS[t.tipo][0], veiculo_id: t.veiculo_id || "", fornecedor: t.fornecedor || "", parcelas: "", recorrente: false, arquivo: null, temComprovante: !!t.comprovante });
  };

  const add = async (tipo) => {
    if (!form.descricao.trim() || !form.valor) return toast.error("Preencha descrição e valor.");
    if (form.categoria === "Veículo" && !form.veiculo_id) return toast.error("Selecione o veículo do custo.");
    setSaving(true);
    try {
      if (form.editId) {
        const payload = {
          descricao: form.descricao,
          valor: Number(form.valor),
          vencimento: form.vencimento,
          categoria: form.categoria,
          fornecedor: form.fornecedor || "",
          veiculo_id: form.categoria === "Veículo" ? form.veiculo_id : null,
        };
        if (form.arquivo) {
          const fd = new FormData();
          fd.append("file", form.arquivo);
          const up = await axios.post(`${API}/upload`, fd, { headers });
          payload.comprovante = up.data.url;
        }
        await axios.put(`${API}/transactions/${form.editId}`, payload, { headers });
        toast.success(form.arquivo ? "Lançamento atualizado com comprovante." : "Lançamento atualizado.");
      } else {
        let comprovante = null;
        if (form.arquivo) {
          const fd = new FormData();
          fd.append("file", form.arquivo);
          const up = await axios.post(`${API}/upload`, fd, { headers });
          comprovante = up.data.url;
        }
        await axios.post(`${API}/transactions`, {
          tipo,
          descricao: form.descricao,
          valor: Number(form.valor),
          vencimento: form.vencimento,
          categoria: form.categoria,
          fornecedor: form.fornecedor || "",
          comprovante,
          recorrente: !!form.recorrente && !(Number(form.parcelas) > 1),
          parcelas: Number(form.parcelas) > 1 ? Number(form.parcelas) : 1,
          veiculo_id: form.categoria === "Veículo" ? form.veiculo_id : null,
        }, { headers });
        toast.success(Number(form.parcelas) > 1 ? `${form.parcelas} parcelas lançadas, uma por mês.` : form.recorrente ? "Conta fixa lançada — repete todo mês sozinha." : "Conta lançada.");
      }
      setForm({ tipo, descricao: "", valor: "", vencimento: hoje, categoria: CATS[tipo][0], veiculo_id: "", fornecedor: "", parcelas: "", recorrente: false, arquivo: null });
      load();
    } catch { toast.error("Erro ao salvar lançamento."); }
    finally { setSaving(false); }
  };

  const toggle = async (t) => {
    try {
      await axios.patch(`${API}/transactions/${t.id}`, { status: t.status === "pago" ? "pendente" : "pago" }, { headers });
      setTxs((ts) => ts.map((x) => (x.id === t.id ? { ...x, status: x.status === "pago" ? "pendente" : "pago" } : x)));
    } catch { toast.error("Erro ao atualizar."); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/transactions/${id}`, { headers });
      setTxs((ts) => ts.filter((x) => x.id !== id));
      toast.success("Conta removida.");
    } catch { toast.error("Erro ao remover."); }
  };

  const exportReport = async () => {
    try {
      const res = await axios.get(`${API}/reports/monthly?mes=${mes}`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `relatorio-seed-motors-${mes}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Planilha do mês baixada!");
    } catch { toast.error("Erro ao gerar relatório."); }
  };

  return (
    <div data-testid="financeiro-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-display text-2xl font-bold text-white">Financeiro</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-slate-400">
            Mês:
            <input
              type="month" value={mes} onChange={(e) => setMes(e.target.value)}
              disabled={periodoLivre}
              className="glass rounded-full px-4 py-2 text-sm text-white outline-none [color-scheme:dark] disabled:opacity-40"
              data-testid="fin-mes"
            />
          </label>
          <button
            onClick={() => setFiltrosAbertos((v) => !v)}
            data-testid="fin-filtros-toggle"
            title="Pesquisa personalizada"
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${filtrosAbertos || temFiltro ? "bg-emerald-500 text-emerald-950" : "glass text-slate-300 hover:text-emerald-400"}`}
          >
            <Search className="w-4 h-4" />
          </button>
          <button
            onClick={exportReport} data-testid="fin-export-button"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-sm font-bold px-4 py-2.5 rounded-full transition-colors"
          >
            <FileSpreadsheet className="w-4 h-4" /> Exportar mês
          </button>
        </div>
      </div>

      <AnimatePresence>
        {filtrosAbertos && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="glass rounded-2xl p-5 mb-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="fin-filtros">
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">De</label>
                <input type="date" className={inputCls} value={fDe} onChange={(e) => setFDe(e.target.value)} data-testid="fin-filtro-de" />
              </div>
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">Até</label>
                <input type="date" className={inputCls} value={fAte} onChange={(e) => setFAte(e.target.value)} data-testid="fin-filtro-ate" />
              </div>
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">Tipo</label>
                <select className={inputCls} value={fTipo} onChange={(e) => setFTipo(e.target.value)} data-testid="fin-filtro-tipo">
                  <option value="todos">Todos</option>
                  <option value="receber">A receber</option>
                  <option value="pagar">A pagar</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">Categoria</label>
                <select className={inputCls} value={fCat} onChange={(e) => setFCat(e.target.value)} data-testid="fin-filtro-categoria">
                  <option value="">Todas</option>
                  {todasCategorias.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">Fornecedor, cliente ou descrição</label>
                <div className="flex gap-2">
                  <input className={inputCls} placeholder="Buscar..." value={fBusca} onChange={(e) => setFBusca(e.target.value)} data-testid="fin-filtro-busca" />
                  {temFiltro && (
                    <button onClick={limparFiltros} data-testid="fin-filtros-limpar" title="Limpar filtros" className="w-10 h-10 shrink-0 rounded-xl glass flex items-center justify-center text-slate-400 hover:text-red-400 transition-colors">
                      <FilterX className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="fin-summary">
        <div className="glass rounded-2xl p-5">
          <p className="text-xs text-slate-400 flex items-center gap-1.5"><ArrowUpCircle className="w-4 h-4 text-emerald-400" /> Entradas (pagas)</p>
          <p className="font-display text-2xl font-bold text-emerald-400 mt-2" data-testid="fin-entradas">{formatBRL(entradas)}</p>
        </div>
        <div className="glass rounded-2xl p-5">
          <p className="text-xs text-slate-400 flex items-center gap-1.5"><ArrowDownCircle className="w-4 h-4 text-red-400" /> Saídas (pagas)</p>
          <p className="font-display text-2xl font-bold text-red-400 mt-2" data-testid="fin-saidas">{formatBRL(saidas)}</p>
        </div>
        <div className="glass rounded-2xl p-5">
          <p className="text-xs text-slate-400 flex items-center gap-1.5"><Scale className="w-4 h-4 text-emerald-400" /> Saldo {periodoLivre ? "do período" : "do mês"}</p>
          <p className={`font-display text-2xl font-bold mt-2 ${entradas - saidas >= 0 ? "text-white" : "text-red-400"}`} data-testid="fin-saldo">{formatBRL(entradas - saidas)}</p>
        </div>
      </div>

      {txs && (
        <div className="glass rounded-2xl p-5 mb-6" data-testid="fin-acumulado">
          <p className="text-xs text-slate-400 mb-4 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4 text-emerald-400" /> Saldo acumulado — últimos 6 meses
          </p>
          <div className="grid grid-cols-6 gap-2 sm:gap-3 items-end h-24">
            {acumulado.map(({ m, acc }) => {
              const positivo = acc >= 0;
              const altura = Math.max(6, Math.round((Math.abs(acc) / maxAcc) * 100));
              const [ano, mesNum] = m.split("-");
              return (
                <div key={m} className="flex flex-col items-center gap-1.5 min-w-0" data-testid={`fin-acumulado-${m}`}>
                  <span className={`text-[10px] sm:text-[11px] font-semibold whitespace-nowrap ${positivo ? "text-emerald-400" : "text-red-400"}`}>
                    {formatBRL(acc)}
                  </span>
                  <div
                    className={`w-full max-w-10 rounded-t-md ${positivo ? "bg-emerald-500/70" : "bg-red-500/70"}`}
                    style={{ height: `${altura}%` }}
                  />
                  <span className="text-[10px] text-slate-500">{MESES_CURTOS[Number(mesNum) - 1]}/{ano.slice(2)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {Object.keys(gastosPorCategoria).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="fin-categorias">
          {Object.entries(gastosPorCategoria).map(([c, total]) => (
            <span key={c} className="glass rounded-full px-4 py-2 text-xs text-slate-300">
              {c}: <span className="font-semibold text-red-400">{formatBRL(total)}</span>
            </span>
          ))}
        </div>
      )}

      {txs === null ? (
        <div className="h-64 rounded-2xl bg-[#131620] animate-pulse" />
      ) : (
        <div className={`grid gap-4 ${fTipo === "todos" ? "lg:grid-cols-2" : "lg:grid-cols-1 max-w-3xl"}`}>
          {fTipo !== "pagar" && (
            <FinColumn tipo="receber" titulo="Contas a receber" icon={ArrowUpCircle} color="text-emerald-400" items={filtradas.filter((t) => t.tipo === "receber")} form={form} setForm={setForm} saving={saving} add={add} toggle={toggle} remove={remove} onEdit={startEdit} vehicles={vehicles} vehicleName={vehicleName} />
          )}
          {fTipo !== "receber" && (
            <FinColumn tipo="pagar" titulo="Contas a pagar" icon={ArrowDownCircle} color="text-red-400" items={filtradas.filter((t) => t.tipo === "pagar")} form={form} setForm={setForm} saving={saving} add={add} toggle={toggle} remove={remove} onEdit={startEdit} vehicles={vehicles} vehicleName={vehicleName} />
          )}
        </div>
      )}
    </div>
  );
};
